import React from 'react';
import './AnalyticsDashboard.css';

const PR_COLORS = { low: '#22c55e', medium: '#3b82f6', high: '#f59e0b', critical: '#ef4444' };

const AnalyticsDashboard = ({ analytics, stats = null }) => {
  // Если analytics null или undefined — показываем заглушку
  if (!analytics) {
    return (
      <div className="analytics-dashboard">
        <div className="analytics-empty">
          <p>⏳ Загрузка данных аналитики...</p>
        </div>
      </div>
    );
  }

  const maxVisits = analytics.dailyVisits?.length
    ? Math.max(...analytics.dailyVisits.map(d => d.visits))
    : 1;

  return (
    <div className="analytics-dashboard">
      <div className="analytics-grid">
        <div className="analytics-card total">
          <span className="analytics-value">{analytics.totalVisits}</span>
          <span className="analytics-label">Всего визитов</span>
        </div>
        <div className="analytics-card unique">
          <span className="analytics-value">{analytics.uniqueVisits}</span>
          <span className="analytics-label">Уникальные</span>
        </div>
        <div className="analytics-card online">
          <span className="analytics-value">{analytics.currentVisitors}</span>
          <span className="analytics-label">Онлайн сейчас</span>
        </div>
      </div>

      {stats && (
        <div className="analytics-section">
          <h3>📊 Статистика заявок</h3>
          <div className="stats-grid">
            <div className="stat-card"><span className="stat-value">{stats.total}</span><span className="stat-label">Всего</span></div>
            <div className="stat-card"><span className="stat-value">{stats.new}</span><span className="stat-label">Новых</span></div>
            <div className="stat-card"><span className="stat-value">{stats.inProgress}</span><span className="stat-label">В работе</span></div>
            <div className="stat-card"><span className="stat-value">{stats.completed}</span><span className="stat-label">Завершено</span></div>
            <div className="stat-card"><span className="stat-value">{stats.overdue || 0}</span><span className="stat-label">Просрочено</span></div>
            {stats.avgCompletionTime !== null && (
              <div className="stat-card"><span className="stat-value">{stats.avgCompletionTime} ч.</span><span className="stat-label">Среднее время</span></div>
            )}
            {stats.slaCompliance !== null && (
              <div className="stat-card"><span className="stat-value">{stats.slaCompliance}%</span><span className="stat-label">SLA</span></div>
            )}
          </div>

          {stats.byPriority && Object.keys(stats.byPriority).length > 0 && (
            <div className="analytics-subsection">
              <h4>По приоритетам</h4>
              <div className="bar-chart">
                {Object.entries(stats.byPriority).map(([priority, count]) => {
                  const total = Object.values(stats.byPriority).reduce((a, b) => a + b, 0);
                  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                  return (
                    <div key={priority} className="bar-item">
                      <span className="bar-label">
                        <span className="bar-color" style={{ background: PR_COLORS[priority] || '#3b82f6' }}></span>
                        {priority}
                      </span>
                      <span className="bar-value">{count} ({pct}%)</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {analytics.dailyVisits?.length > 0 && (
        <div className="analytics-section">
          <h3>📈 Посещения по дням</h3>
          <div className="daily-chart">
            {analytics.dailyVisits.map(d => (
              <div key={d.date} className="day-bar">
                <div
                  className="bar"
                  style={{ height: `${Math.max(10, (d.visits / maxVisits) * 120)}px` }}
                  title={`${d.date}: ${d.visits} визитов, ${d.unique} уникальных`}
                ></div>
                <span className="date">{d.date.slice(5)}</span>
                <span className="count">{d.visits}</span>
                <span className="unique-count">{d.unique}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {analytics.browsers?.length > 0 && (
        <div className="analytics-section">
          <h3>🌐 Браузеры</h3>
          <div className="devices-grid">
            {analytics.browsers.map(b => {
              const total = analytics.browsers.reduce((s, x) => s + x.count, 0);
              const pct = total > 0 ? Math.round((b.count / total) * 100) : 0;
              return (
                <div key={b._id} className="device-item">
                  <span className="device-name">{b._id}</span>
                  <div className="device-bar-bg">
                    <div className="device-bar-fill" style={{ width: `${pct}%` }}></div>
                  </div>
                  <span className="device-count">{b.count} ({pct}%)</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {analytics.os?.length > 0 && (
        <div className="analytics-section">
          <h3>💻 Операционные системы</h3>
          <div className="devices-grid">
            {analytics.os.map(o => {
              const total = analytics.os.reduce((s, x) => s + x.count, 0);
              const pct = total > 0 ? Math.round((o.count / total) * 100) : 0;
              return (
                <div key={o._id} className="device-item">
                  <span className="device-name">{o._id}</span>
                  <div className="device-bar-bg">
                    <div className="device-bar-fill os" style={{ width: `${pct}%` }}></div>
                  </div>
                  <span className="device-count">{o.count} ({pct}%)</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {analytics.devices?.length > 0 && (
        <div className="analytics-section">
          <h3>📱 Устройства</h3>
          <div className="devices-grid">
            {analytics.devices.map(d => {
              const total = analytics.devices.reduce((s, x) => s + x.count, 0);
              const pct = total > 0 ? Math.round((d.count / total) * 100) : 0;
              return (
                <div key={d._id} className="device-item">
                  <span className="device-name">{d._id === 'desktop' ? '💻' : d._id === 'mobile' ? '📱' : '📟'} {d._id}</span>
                  <div className="device-bar-bg">
                    <div className="device-bar-fill device" style={{ width: `${pct}%` }}></div>
                  </div>
                  <span className="device-count">{d.count} ({pct}%)</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;